import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  CaretLeftIcon,
  CaretRightIcon,
  FlagIcon,
  GaugeIcon,
  MapPinIcon,
  PlusIcon,
  TrashIcon,
  TruckTrailerIcon,
  XIcon,
} from '@phosphor-icons/react';

import { Button } from '../../../../components/button/Button';
import { Input } from '../../../../components/input/Input';
import { TextArea } from '../../../../components/textarea/TextArea';
import { TrailerCombobox } from '../../../trailer/TrailerCombobox';
import classNames from '../../../../utils/classnames';
import { getDisplayError } from '../../../../utils/get-display-error';
import { formatDayMonth, formatWeekday } from '../../../../utils/date';
import { formatNumber } from '../../../../utils/number';
import {
  savePlanningBoardWeeklyDay,
  type PlanningBoardWeeklySaveDayResponse,
  type WeeklyTrailer,
  type WeeklyTruck,
} from '../../models';
import { TripStatusSelect } from './TripStatus';
import { createTripDraft, isBlankDraft, parseDraftKm, type TripDraft } from './tripDrafts';

interface DayPanelProps {
  truck: WeeklyTruck;
  date: string;
  drafts: TripDraft[];
  onDraftsChange: (drafts: TripDraft[]) => void;
  trailer: WeeklyTrailer | null;
  onTrailerChange: (trailer: WeeklyTrailer | null) => void;
  /** The drafts or trailer differ from what is saved. */
  canSave: boolean;
  onClose: () => void;
  onPrevDay?: () => void;
  onNextDay?: () => void;
  onSaved: (saved: PlanningBoardWeeklySaveDayResponse) => void;
}

/** Side panel to edit the trailer and trips of one truck on one day; the page owns the drafts and closes it on save. */
export const DayPanel: React.FC<DayPanelProps> = ({
  truck,
  date,
  drafts,
  onDraftsChange,
  trailer,
  onTrailerChange,
  canSave,
  onClose,
  onPrevDay,
  onNextDay,
  onSaved,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const dayTotal = drafts.reduce((sum, d) => sum + (parseDraftKm(d.km) ?? 0), 0);

  const updateDraft = (key: string, patch: Partial<TripDraft>) =>
    onDraftsChange(drafts.map((d) => (d.key === key ? { ...d, ...patch } : d)));

  const save = async () => {
    const filled = drafts.filter((d) => !isBlankDraft(d));
    if (filled.some((d) => parseDraftKm(d.km) == null)) {
      setShowErrors(true);
      toast.error('Kilometers moet een geheel getal zijn');
      return;
    }
    setIsSaving(true);
    try {
      const response = await savePlanningBoardWeeklyDay({
        supplierTruckId: truck.id,
        date,
        trailerId: trailer?.id ?? null,
        trips: filled.map((d) => ({
          loading: d.loading,
          loadingStatus: d.loadingStatus,
          unloading: d.unloading,
          unloadingStatus: d.unloadingStatus,
          km: parseDraftKm(d.km) ?? 0,
        })),
      });
      onSaved(response);
      toast.success('Dag opgeslagen');
    } catch (err) {
      toast.error(`Kon dag niet opslaan: ${getDisplayError(err)}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <aside className="absolute inset-y-0 right-0 z-table-bar flex w-[400px] flex-col border-l border-border bg-surface shadow-lg">
      <header className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div className="min-w-0">
          <h2 className="heading-three truncate">
            {formatWeekday(date)} {formatDayMonth(date)}
          </h2>
          <p className="caption-text truncate">
            {truck.supplierName} · {truck.licensePlate}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex">
            <PanelIconButton label="Vorige dag" onClick={onPrevDay} className="rounded-l-md">
              <CaretLeftIcon />
            </PanelIconButton>
            <PanelIconButton label="Volgende dag" onClick={onNextDay} className="-ml-px rounded-r-md">
              <CaretRightIcon />
            </PanelIconButton>
          </div>
          <PanelIconButton label="Sluiten" onClick={onClose} className="rounded-md border-transparent">
            <XIcon />
          </PanelIconButton>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-5 pb-5 pt-1">
        <section className="border-b border-border py-[18px]">
          <PanelField icon={<TruckTrailerIcon />} label="Trailer">
            <TrailerCombobox value={trailer} onChange={onTrailerChange} />
          </PanelField>
        </section>

        {drafts.map((draft, idx) => {
          const kmInvalid = showErrors && !isBlankDraft(draft) && parseDraftKm(draft.km) == null;
          return (
            <section key={draft.key} className="flex flex-col gap-3 border-b border-border py-[18px]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-fg">Rit {idx + 1}</span>
                <button
                  type="button"
                  title="Rit verwijderen"
                  aria-label={`Rit ${idx + 1} verwijderen`}
                  onClick={() => onDraftsChange(drafts.filter((d) => d.key !== draft.key))}
                  className="text-fg-muted transition-colors hover:text-negative"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
              <PanelField
                icon={<MapPinIcon />}
                label="Laden"
                htmlFor={`${draft.key}-loading`}
                action={
                  <TripStatusSelect
                    value={draft.loadingStatus}
                    onChange={(loadingStatus) => updateDraft(draft.key, { loadingStatus })}
                    title="Status laden"
                    context={`Rit ${idx + 1}`}
                  />
                }
              >
                <TextArea
                  id={`${draft.key}-loading`}
                  rows={2}
                  value={draft.loading}
                  onChange={(loading) => updateDraft(draft.key, { loading })}
                  placeholder="Plaats, uur, TD-nummer, notities…"
                />
              </PanelField>
              <PanelField
                icon={<FlagIcon />}
                label="Lossen"
                htmlFor={`${draft.key}-unloading`}
                action={
                  <TripStatusSelect
                    value={draft.unloadingStatus}
                    onChange={(unloadingStatus) => updateDraft(draft.key, { unloadingStatus })}
                    title="Status lossen"
                    context={`Rit ${idx + 1}`}
                  />
                }
              >
                <TextArea
                  id={`${draft.key}-unloading`}
                  rows={2}
                  value={draft.unloading}
                  onChange={(unloading) => updateDraft(draft.key, { unloading })}
                  placeholder="Plaats, uur, TD-nummer, notities…"
                />
              </PanelField>
              <PanelField icon={<GaugeIcon />} label="Kilometers" htmlFor={`${draft.key}-km`}>
                <div className="w-[120px]">
                  <Input
                    id={`${draft.key}-km`}
                    type="number"
                    value={draft.km}
                    onChange={(km) => updateDraft(draft.key, { km })}
                    isInvalid={kmInvalid}
                    placeholder="0"
                    iconRight={<span className="text-xs">km</span>}
                  />
                </div>
              </PanelField>
            </section>
          );
        })}

        <div className="pt-4">
          <button
            type="button"
            onClick={() => onDraftsChange([...drafts, createTripDraft()])}
            className="flex h-[38px] w-full items-center justify-center gap-2 rounded-md border border-border-strong bg-surface-sunken text-xs font-medium text-fg-secondary transition-colors hover:bg-surface-hover"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Rit toevoegen
          </button>
        </div>
      </div>

      <footer className="flex items-center justify-between border-t border-border px-5 py-3.5">
        <div className="flex items-baseline gap-1.5">
          <span className="label-text">Dagtotaal</span>
          <span className="text-sm font-semibold text-fg">{formatNumber(dayTotal, 0)} km</span>
        </div>
        <Button color="primary" onTrigger={save} isLoading={isSaving} isDisabled={!canSave}>
          Opslaan
        </Button>
      </footer>
    </aside>
  );
};

interface PanelFieldProps {
  icon: React.ReactElement;
  label: string;
  htmlFor?: string;
  /** Shown at the right end of the label row. */
  action?: React.ReactNode;
  children: React.ReactNode;
}

const PanelField: React.FC<PanelFieldProps> = ({ icon, label, htmlFor, action, children }) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex items-center justify-between gap-1.5">
      <label htmlFor={htmlFor} className="label-text flex items-center gap-1.5">
        <span className="[&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span>
        {label}
      </label>
      {action}
    </div>
    {children}
  </div>
);

interface PanelIconButtonProps {
  label: string;
  onClick?: () => void;
  className?: string;
  children: React.ReactElement;
}

const PanelIconButton: React.FC<PanelIconButtonProps> = ({ label, onClick, className, children }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    onClick={onClick}
    disabled={!onClick}
    className={classNames(
      'flex size-[30px] items-center justify-center border border-border text-fg-secondary transition-colors hover:bg-surface-hover disabled:opacity-50 disabled:hover:bg-transparent [&>svg]:h-3.5 [&>svg]:w-3.5',
      className,
    )}
  >
    {children}
  </button>
);
